/**
 * Fachliche Auswertung von Fristen (in der UI „Termine") – die Komponenten bekommen
 * fertige Werte.
 */
import { DeadlineItem } from "../types";

/** Ob eine manuelle Frist abgehakt ist; abgeleitete sind nie abgehakt, sie fallen weg */
export const isDone = (deadline: DeadlineItem): boolean => !!deadline.done_at;

/** Ob es ein persönlicher Termin ist – ohne Projekt, er gehört dem Verantwortlichen */
export const isPersonal = (deadline: DeadlineItem): boolean =>
  deadline.reference_type === "personal";

/**
 * Ob es der eigene persönliche Termin ist – nur ihn pflegt man; fremde zeigt die Teamübersicht
 * lediglich an. `userId` ist die IAM-ID des angemeldeten Benutzers.
 */
export const isOwnPersonal = (
  deadline: DeadlineItem,
  userId: string | undefined,
): boolean =>
  isPersonal(deadline) &&
  !!userId &&
  deadline.verantwortlich?.external_id === userId;

/** Die Seite eines Bezugs: Nachweis, Antrag oder – ohne beide – die Projektübersicht */
export const referencePath = (reference: {
  projectId: string;
  permitProcedureId?: string | null;
  evidenceId?: string | null;
}): string => {
  const base = `/project/${reference.projectId}`;
  if (reference.permitProcedureId && reference.evidenceId) {
    return `${base}/permits/${reference.permitProcedureId}/evidences?nachweis=${reference.evidenceId}`;
  }
  if (reference.permitProcedureId) {
    return `${base}/permits/${reference.permitProcedureId}/elia`;
  }
  return `${base}/overview`;
};

/** Beschriftung des Links zur Seite des Bezugs, passend zu `referencePath` */
export const referenceLinkLabel = (reference: {
  permitProcedureId?: string | null;
  evidenceId?: string | null;
}): string =>
  reference.evidenceId
    ? "Zum Nachweis"
    : reference.permitProcedureId
      ? "Zum Antrag"
      : "Zur Projektübersicht";

/** Wohin ein Klick auf die Frist führt: zu ihrem Bezug; `null` beim persönlichen Termin */
export const deadlinePath = (deadline: DeadlineItem): string | null =>
  isPersonal(deadline) || !deadline.project_id
    ? null
    : referencePath({
        projectId: deadline.project_id,
        permitProcedureId: deadline.permit_procedure_id,
        evidenceId: deadline.evidence_id,
      });

/** Der Bezug als Text für die Spalte „Bezug": „Nachweis: Schallgutachten", „Projekt" */
export const referenceText = (deadline: DeadlineItem): string => {
  if (deadline.reference_type === "project") return "Projekt";
  if (deadline.reference_type === "personal") return "Persönlich";
  const kind = deadline.reference_type === "evidence" ? "Nachweis" : "Antrag";
  return deadline.reference_label
    ? `${kind}: ${deadline.reference_label}`
    : kind;
};

/**
 * Der Pfad zum Bezug: Projekt › Antrag › Nachweis, so weit die Frist reicht. Leer beim
 * persönlichen Termin.
 */
export const referenceBreadcrumb = (deadline: DeadlineItem): string[] => {
  if (isPersonal(deadline)) return [];
  return [
    deadline.project_name,
    deadline.permit_procedure_label,
    deadline.reference_type === "evidence" ? deadline.reference_label : null,
  ].filter((part): part is string => !!part);
};

/** Was man an einer Frist direkt in der Liste ändern darf */
export interface DeadlineEditing {
  dueDate: boolean;
  responsible: boolean;
  /** Bei einer abgeleiteten Frist die Notiz des Nachweises; der Antrag hat keine */
  note: boolean;
}

/**
 * Wer Datum und Verantwortlichen ändern darf – dieselben Rechte wie die Server-Endpunkte:
 * den eigenen persönlichen Termin nur im Datum (verantwortlich ist man selbst), eine Frist am
 * Projekt mit Schreibrecht an Projekten, eine an Antrag oder Nachweis und jede abgeleitete
 * mit Schreibrecht an der Genehmigung. Eine abgeleitete Frist ändert das Feld am Nachweis,
 * das sie trägt, und dessen Notiz. Die Notiz folgt überall dem Recht am Datum.
 */
export const deadlineEditing = (
  deadline: DeadlineItem,
  rights: {
    userId?: string;
    canEditProjects: boolean;
    canEditPermits: boolean;
  },
): DeadlineEditing => {
  if (isPersonal(deadline)) {
    return {
      dueDate: isOwnPersonal(deadline, rights.userId),
      responsible: false,
      note: isOwnPersonal(deadline, rights.userId),
    };
  }
  if (deadline.source === "derived") {
    const allowed = rights.canEditPermits && !!deadline.due_field;
    // Die Frist des Antragsstatus (ADR 011): Datum und Bearbeitung am Antrag, ohne Notiz und Problem.
    return {
      dueDate: allowed,
      responsible: allowed,
      note: allowed && !!deadline.evidence_id,
    };
  }
  const allowed =
    deadline.reference_type === "project"
      ? rights.canEditProjects
      : rights.canEditPermits;
  return { dueDate: allowed, responsible: allowed, note: allowed };
};

/**
 * Ob die Frist an genau dem Bezug hängt, dessen Seite man gerade sieht – dann führt ein Klick
 * nirgendwohin. Ohne Antrag und Nachweis ist es die Projektübersicht.
 */
export const isOnCurrentReference = (
  deadline: DeadlineItem,
  current: { permitProcedureId?: string | null; evidenceId?: string | null },
): boolean => {
  if (current.evidenceId) return deadline.evidence_id === current.evidenceId;
  if (current.permitProcedureId)
    return deadline.reference_type === "permit_procedure";
  return deadline.reference_type === "project";
};

/** Manuelle Fristen, die mit einem Nachweis bzw. Antrag gelöscht würden */
export const manualDeadlinesOf = (
  deadlines: DeadlineItem[],
  reference: { evidenceId?: string; permitProcedureId?: string },
): DeadlineItem[] =>
  deadlines.filter(
    (deadline) =>
      deadline.source === "manual" &&
      (reference.evidenceId
        ? deadline.evidence_id === reference.evidenceId
        : deadline.permit_procedure_id === reference.permitProcedureId),
  );

/** Satz für Bestätigungsdialoge: „Dabei wird 1 Termin gelöscht." – leer ohne Termine */
export const deletedDeadlinesNotice = (count: number): string =>
  count === 0
    ? ""
    : count === 1
      ? "Dabei wird 1 Termin gelöscht."
      : `Dabei werden ${count} Termine gelöscht.`;

/** Die offenen manuellen Fristen eines Nachweises mit Datum, die früheste zuerst */
export const openManualDeadlines = (
  deadlines: DeadlineItem[],
  evidenceId: string,
): DeadlineItem[] =>
  manualDeadlinesOf(deadlines, { evidenceId })
    .filter((deadline) => !isDone(deadline) && deadline.zieltermin)
    .sort((a, b) => (a.zieltermin ?? "").localeCompare(b.zieltermin ?? ""));
